import { forwardRef, memo, type ReactNode, useContext, useEffect, type Component } from 'react';
// Material UI imports
import { type BoxProps } from '@mui/material/Box';
// Skillmore UI Components
import type { BreadcrumbItem } from '@empathco/ui-components/src/types';
import { type AppBarVariant, APPBAR_NONE } from '@empathco/ui-components/src/elements/AppBar';
import AppScreen from '@empathco/ui-components/src/elements/AppScreen';
// local imports
import { DataContext } from '../context';
import Header from '../widgets/Header';

type ScreenProps = {
  readonly children?: ReactNode;
  readonly admin?: boolean;
  readonly splash?: boolean;
  readonly fullHeight?: boolean;
  readonly dashboard?: boolean;
  readonly breadcrumbs?: BreadcrumbItem[] | null;
  readonly appbar?: AppBarVariant;
  readonly footer?: ReactNode;
  readonly withSettings?: boolean;
  readonly className?: string;
};

const Screen = forwardRef<Component<BoxProps>, ScreenProps>(({
  children,
  admin = false,
  splash = false,
  fullHeight = false,
  dashboard = false,
  breadcrumbs,
  appbar = 'full',
  footer,
  withSettings = false,
  className
}, ref) => {
  const { requireSettings } = useContext(DataContext);

  useEffect(() => {
    if (withSettings) requireSettings?.();
  }, [withSettings, requireSettings]);

  return (
    <AppScreen
        ref={ref}
        splash={splash}
        fullHeight={fullHeight}
        dashboard={dashboard}
        appbar={appbar}
        className={className}
        header={appbar !== APPBAR_NONE && (
          <Header
              breadcrumbs={breadcrumbs}
              variant={appbar}
              admin={admin}
          />
        )}
        footer={footer}
    >
      {children}
    </AppScreen>
  );
});

Screen.displayName = 'Screen';

export default memo(Screen);
